/* Session — employees-only via Google SSO behind Cloudflare Access.
   The public login card lives at `/` (no Access). Everything under `/app` is
   authed: on load we call `/api/me`, which returns a VERIFIED Access identity
   or 401. No identity → the dashboard shell must NOT mount (only the card
   renders); identity → the shell renders and connectors return live data.
   This closes the client-side bypass where the SPA could route from `/` into
   `/app` without a real server navigation (so Access was never challenged).

   Local dev / QA has no Access in front of it, so we open the shell there —
   keyed on the *hostname* (localhost/127.0.0.1), which a request to the prod
   host can never spoof, so production stays strict. The preview runs behind
   Access, so reaching it already means a verified identity. */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { User } from '../lib/contract';
import { PLACEHOLDER } from '../lib/placeholder';

type AuthStatus = 'loading' | 'authed' | 'anon';

const isLocalDev =
  typeof window !== 'undefined' && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

interface Session {
  /** Real /api/me result. 'loading' only on the network round-trip. */
  status: AuthStatus;
  /** Whether the dashboard may render: a verified identity, or local dev. */
  openShell: boolean;
  signedIn: boolean;
  user: User;
  signOut: () => void;
}

const SessionCtx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  // Local dev: no Access to ask — resolve immediately as anon-but-open so the
  // login card is still screenshot-able while /app renders on sample data.
  const [status, setStatus] = useState<AuthStatus>(isLocalDev ? 'anon' : 'loading');
  const [user, setUser] = useState<User>(PLACEHOLDER.user);

  useEffect(() => {
    if (isLocalDev) return;
    let alive = true;
    fetch('/api/me', { headers: { accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { email?: string } | null) => {
        if (!alive) return;
        if (j && j.email) {
          setUser((u) => ({ ...u, email: j.email as string }));
          setStatus('authed');
        } else {
          setStatus('anon');
        }
      })
      .catch(() => {
        if (alive) setStatus('anon');
      });
    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo<Session>(
    () => ({
      status,
      openShell: isLocalDev || status === 'authed',
      signedIn: status === 'authed',
      user,
      // Real Access logout truly ends the session; locally just return home.
      signOut: () => window.location.assign(isLocalDev ? '/' : '/cdn-cgi/access/logout'),
    }),
    [status, user],
  );

  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession(): Session {
  const s = useContext(SessionCtx);
  if (!s) throw new Error('useSession must be used within <SessionProvider>');
  return s;
}

/** Gate for the authed views: never mounts the dashboard without a verified
    Access identity (or local dev). While /api/me is in flight, show a splash;
    if there's no identity, bounce to the public login card. */
export function RequireAccess({ children }: { children: ReactNode }) {
  const { status, openShell } = useSession();
  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--bg)', color: 'var(--muted)', fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.08em' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--border)', borderTopColor: 'var(--primary)', animation: 'ct-spin 0.7s linear infinite' }} />
          verifying access…
        </span>
      </div>
    );
  }
  if (!openShell) return <Navigate to="/" replace />;
  return <>{children}</>;
}
